import { View, StyleSheet, Platform } from 'react-native'
import { SectionTitle } from '../../../../app/components/Admin/SectionTitle'
import { DS } from '../../../../app/design-system'
import { Pressable, ScrollView } from 'react-native-gesture-handler'
import {
  PlayerInformation,
  EMPTY_PLAYER_DASHBOARD_STATS
} from '../../../../app/components/Admin/User/PlayerInformation'
import { PlayerGamesTable } from './PlayerGamesTable'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'expo-router'
import { useState } from 'react'
import { RoleManagement } from './RoleManagement'

function decodeId(id: string) {
  try {
    return decodeURIComponent(id)
  } catch {
    return id
  }
}

const CopyButton = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (Platform.OS === 'web') {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  return (
    <View style={styles.idRow}>
      <DS.Text variant="Body Small/Medium" color="Brand/Dark Grey">
        {label}:
      </DS.Text>
      <DS.Text variant="Body Small/Regular" selectable>
        {value || 'N/A'}
      </DS.Text>
      {value ? (
        <Pressable onPress={handleCopy} style={styles.copyButton}>
          <DS.Text variant="Body Small/Medium" color="Admin/Icon">
            {copied ? 'Copied!' : 'Copy'}
          </DS.Text>
        </Pressable>
      ) : null}
    </View>
  )
}

export const UserSection = observer(({ id }: { id: string }) => {
  const router = useRouter()
  const decodedId = decodeId(id)

  const handleBack = () => {
    if (router.canGoBack()) {
      router.back()
      return
    }

    router.navigate('/dashboard')
  }

  const dashboardUser = useConvexQuery(api.players.queries.getDashboardUserData, {
    id: decodedId
  })

  const auth0Id = dashboardUser?.identity?.auth0Id ?? decodedId
  const convexPlayerId = dashboardUser?.identity?.convexPlayerId ?? ''
  const playerName = dashboardUser?.identity?.playerName ?? ''
  const stats = dashboardUser?.stats ?? EMPTY_PLAYER_DASHBOARD_STATS

  return (
    <ScrollView>
      <View style={styles.container}>
        <DS.Frame style={styles.heading}>
          <View style={styles.navigationActions}>
            <Pressable onPress={handleBack} style={styles.backButton}>
              <DS.Icon name="Arrow---left" size={12} />
              <DS.Text variant="Body Medium/Regular">Back</DS.Text>
            </Pressable>
            <Pressable onPress={() => router.navigate('/dashboard')} style={styles.dashboardButton}>
              <DS.Text variant="Body Small/Medium" color="Admin/Icon">
                Dashboard
              </DS.Text>
            </Pressable>
          </View>
          <SectionTitle title={playerName} />
          <RoleManagement key={auth0Id} id={auth0Id} disabled={dashboardUser === undefined} />
        </DS.Frame>
        <View style={styles.idsContainer}>
          <CopyButton label="Auth0 ID" value={auth0Id} />
          <CopyButton label="Convex Player ID" value={convexPlayerId} />
        </View>
        <DS.Frame style={styles.body}>
          <PlayerInformation stats={stats} />
          <DS.Frame style={styles.cardsContainer}>
            <PlayerGamesTable id={auth0Id} />
          </DS.Frame>
        </DS.Frame>
      </View>
    </ScrollView>
  )
})

const styles = StyleSheet.create({
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  navigationActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12
  },
  dashboardButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  },
  container: {
    gap: 32
  },
  heading: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 32
  },
  idsContainer: {
    flexDirection: 'row',
    gap: 24,
    paddingHorizontal: 4
  },
  idRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  copyButton: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  },
  body: {
    gap: 16
  },
  cardsContainer: {
    flexDirection: 'row',
    gap: 16
  }
})
